/**
 * bd vc8u — DOM-recorder flush-outcome policy.
 *
 * Pure decision table for what to do after a batch flush attempt:
 *
 *   ┌─────────────────────────────────┬──────────┬─────────────────┐
 *   │ outcome                         │ action   │ nextFailures    │
 *   ├─────────────────────────────────┼──────────┼─────────────────┤
 *   │ network ok, http ok             │ clear    │ 0               │
 *   │ network ok, http NOT ok         │ drop     │ unchanged       │
 *   │ network err, failures < cap     │ requeue  │ failures + 1    │
 *   │ network err, failures ≥ cap     │ drop     │ 0               │
 *   └─────────────────────────────────┴──────────┴─────────────────┘
 *
 * Why drop on HTTP 4xx/5xx: re-queuing a too-large body / expired-token /
 * server-side issue is a forever-loop. Replay capture is best-effort; the
 * timeline survives a dropped batch.
 *
 * Why cap retries: a shopper on flaky network previously could accumulate
 * events for the full session, growing the buffer unboundedly until even
 * a single retry's body was too big to ever succeed.
 */

export interface FlushOutcome {
  /** True when fetch threw (offline / DNS / abort). */
  networkError: boolean;
  /** True when the server responded with a 2xx. Meaningless if networkError. */
  httpOk: boolean;
  /** Consecutive failed-flush attempts BEFORE this one. */
  consecutiveFailures: number;
  /** Max consecutive failures before the batch is dropped. */
  maxFailures: number;
}

export interface FlushDecision {
  action: 'clear' | 'requeue' | 'drop';
  /** What to set state.consecutiveFailures to after applying the action. */
  nextFailures: number;
}

export function classifyFlushResult(o: FlushOutcome): FlushDecision {
  if (!o.networkError) {
    if (o.httpOk) return { action: 'clear', nextFailures: 0 };
    // Server-side rejection (e.g. 413 too large, 401 expired) — drop. Keep
    // the failure counter where it was so a transient network blip after a
    // 4xx isn't conflated with the retry budget.
    return { action: 'drop', nextFailures: o.consecutiveFailures };
  }
  // Network error.
  if (o.consecutiveFailures + 1 < o.maxFailures + 1 && o.consecutiveFailures < o.maxFailures) {
    return { action: 'requeue', nextFailures: o.consecutiveFailures + 1 };
  }
  // Cap reached — drop the batch and reset the counter so a single bad
  // minute of network can't permanently disable the recorder.
  return { action: 'drop', nextFailures: 0 };
}
